import type { ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { LocaleLink } from "./LocaleLink";
import { Reveal } from "./Reveal";
import { Markdown } from "./Markdown";
import { mediaUrl, normalizeMediaUrl } from "@/lib/media";
import { formatDate, type LocalizedContent } from "@/lib/cms.types";
import { useT } from "@/lib/i18n-dict";
import { ArticleMedia } from "./ArticleMedia";
import { blogIllustrationAlt, blogIllustrationUrl } from "@/content/blog-illustrations";

export function PageHero({
  eyebrow,
  title,
  lead,
}: {
  eyebrow: string;
  title: string;
  lead?: string;
}) {
  const t = useT();
  return (
    <header className="hero-glow border-b border-border/60 py-20 md:py-28">
      <div className="container-page">
        <Reveal className="max-w-3xl">
          <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">{t(eyebrow)}</p>
          <h1 className="mt-4 font-display text-4xl font-bold text-balance-tight md:text-6xl">
            {t(title)}
          </h1>
          {lead ? <p className="mt-5 text-lg text-muted-foreground">{t(lead)}</p> : null}
        </Reveal>
      </div>
    </header>
  );
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  const t = useT();
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/70 bg-surface/30 px-8 py-16 text-center md:py-20">
      {icon ? (
        <div className="mb-5 flex size-14 items-center justify-center rounded-full border border-primary/25 bg-primary/10 text-primary [&_svg]:size-6">
          {icon}
        </div>
      ) : null}
      <p className="font-display text-lg font-semibold text-foreground">{t(title)}</p>
      {hint ? <p className="mt-2 max-w-sm text-sm text-muted-foreground">{t(hint)}</p> : null}
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  );
}

/** Shimmering placeholder grid shown while content is loading. */
export function ContentGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div
      className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
      aria-busy="true"
      aria-live="polite"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card"
        >
          <Skeleton className="aspect-[16/9] w-full rounded-none bg-primary/20" />
          <div className="flex flex-1 flex-col gap-3 p-6">
            <Skeleton className="h-3 w-20 bg-primary/20" />
            <Skeleton className="h-6 w-4/5 bg-primary/20" />
            <div className="space-y-2">
              <Skeleton className="h-3.5 w-full bg-primary/20" />
              <Skeleton className="h-3.5 w-full bg-primary/20" />
              <Skeleton className="h-3.5 w-2/3 bg-primary/20" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Placeholder for the search + filter bar above a content grid. */
export function FilterBarSkeleton() {
  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <Skeleton className="h-11 w-full rounded-full bg-primary/20 md:max-w-sm" />
      <div className="flex gap-2">
        <Skeleton className="h-9 w-24 rounded-full bg-primary/20" />
        <Skeleton className="h-9 w-16 rounded-full bg-primary/20" />
        <Skeleton className="h-9 w-16 rounded-full bg-primary/20" />
      </div>
    </div>
  );
}

export function ContentGrid({
  items,
  base,
  locale,
  emptyLabel,
  logoMode = false,
  showDate = true,
}: {
  items: LocalizedContent[];
  base: string;
  locale: string;
  emptyLabel: string;
  /** Logo-style cards: uncropped contain images, no dates. */
  logoMode?: boolean;
  /** Set false to hide the published date on the cards. */
  showDate?: boolean;
}) {
  if (!items.length) {
    return <EmptyState title={emptyLabel} />;
  }

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {items.map((item, index) => {
        const cover =
          item.kind === "blog"
            ? normalizeMediaUrl(blogIllustrationUrl(item.slug, item.cover_url))
            : mediaUrl(item.cover_url);
        const coverAlt = item.kind === "blog" ? blogIllustrationAlt(locale, item.title) : item.title;
        return (
          <Reveal key={item.id} delay={index * 60}>
            <LocaleLink
              to={`${base}/${item.localizedSlug || item.slug}`}
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-colors hover:border-primary/60"
            >
              {cover ? (
                logoMode ? (
                  <div className="flex aspect-[16/9] w-full items-center justify-center bg-surface/40 p-8">
                    <img
                      src={cover}
                      alt={coverAlt}
                      loading="lazy"
                      className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                ) : (
                  <img
                    src={cover}
                    alt={coverAlt}
                    loading="lazy"
                    className="aspect-[16/9] w-full object-cover"
                  />
                )
              ) : null}
              <div className="flex flex-1 flex-col p-6">
                {showDate && !logoMode && item.published_at ? (
                  <p className="text-xs tracking-[0.14em] leading-relaxed text-muted-foreground uppercase">
                    {formatDate(item.published_at, locale)}
                  </p>
                ) : null}
                <h2 className="mt-2 font-display text-xl font-semibold text-balance-tight transition-colors group-hover:text-primary">
                  {item.title}
                </h2>
                {item.excerpt ? (
                  <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{item.excerpt}</p>
                ) : null}
                {item.metrics?.length ? (
                  <dl className="mt-5 flex flex-wrap gap-x-6 gap-y-2 border-t border-border pt-4">
                    {item.metrics.slice(0, 3).map((metric) => (
                      <div key={metric.label}>
                        <dt className="sr-only">{metric.label}</dt>
                        <dd className="font-display text-lg font-bold text-primary">
                          {metric.value}
                        </dd>
                        <p className="text-sm text-muted-foreground">{metric.label}</p>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </div>
            </LocaleLink>
          </Reveal>
        );
      })}
    </div>
  );
}

export function ArticleView({
  item,
  backTo,
  backLabel,
  locale,
  showDate = true,
}: {
  item: LocalizedContent;
  backTo: string;
  backLabel: string;
  locale: string;
  showDate?: boolean;
}) {
  const cover =
    item.kind === "blog"
      ? normalizeMediaUrl(blogIllustrationUrl(item.slug, item.cover_url))
      : mediaUrl(item.cover_url);
  const coverAlt = item.kind === "blog" ? blogIllustrationAlt(locale, item.title) : item.title;
  const t = useT();
  return (
    <article className="py-16 md:py-24">
      <div className="container-page">
        <div className="mx-auto max-w-3xl">
          <LocaleLink
            to={backTo}
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
          >
            ← {t(backLabel)}
          </LocaleLink>
          {showDate && item.published_at ? (
            <p className="mt-8 text-xs tracking-[0.14em] leading-relaxed text-muted-foreground uppercase">
              {formatDate(item.published_at, locale)}
            </p>
          ) : null}
          <h1 className="mt-3 font-display text-3xl font-bold text-balance-tight md:text-5xl">
            {item.title}
          </h1>
          {item.excerpt ? (
            <p className="mt-5 text-lg text-muted-foreground">{item.excerpt}</p>
          ) : null}
          {item.metrics?.length ? (
            <dl className="mt-8 grid grid-cols-2 gap-4 rounded-2xl border border-border bg-card p-6 sm:grid-cols-3">
              {item.metrics.map((metric) => (
                <div key={metric.label}>
                  <dd className="font-display text-2xl font-bold text-primary">{metric.value}</dd>
                  <dt className="mt-1 text-sm text-muted-foreground">{metric.label}</dt>
                </div>
              ))}
            </dl>
          ) : null}
        </div>

        {cover ? (
          <img
            src={cover}
            alt={coverAlt}
            className="mx-auto mt-10 w-full max-w-4xl rounded-2xl border border-border object-cover"
          />
        ) : null}

        <div className="mx-auto mt-10 max-w-3xl">
          <Markdown source={item.body} />
        </div>
        <ArticleMedia videoUrl={item.video_url} gallery={item.gallery} title={item.title} />

        <aside className="mx-auto mt-12 max-w-3xl rounded-2xl border border-border bg-surface/40 p-6 md:p-8">
          <p className="text-sm leading-relaxed text-muted-foreground">
            <span className="font-semibold text-foreground">
              © {new Date().getFullYear()} factory.online.
            </span>{" "}
            {t(
              "This content is protected. You may use it internally with attribution to factory.online; republishing or commercial use requires our permission.",
            )}
          </p>
          <LocaleLink
            to="/reusing-content"
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary transition-colors hover:underline"
          >
            {t("Reuse guidelines")} →
          </LocaleLink>
        </aside>
      </div>
    </article>
  );
}
